import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import AdminNavBar from "../components/AdminNavBar";

interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: string; // e.g. "owner", "admin"
  active: boolean;
  createdAt?: string;
  lastLoginAt?: string | null;
}

export default function ManageAdmins() {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

useEffect(() => {
  const loadAdmins = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch admins: ${res.status}`);
      }

      const data: AdminUser[] = await res.json();
      setAdmins(data);
    } catch (err: any) {
      setError(err.message || "Failed to load admins.");
    } finally {
      setLoading(false);
    }
  };

  loadAdmins();
}, []);


  const formatDate = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  return (
    <Layout>
      <AdminNavBar />

      <div className="pt-[110px] min-h-screen bg-fractal-pattern-3 bg-cover bg-fixed p-6">
        <div className="max-w-5xl mx-auto space-y-6 bg-white/90 backdrop-blur-sm rounded-xl p-5 shadow-md border border-gray-200">
          <h1 className="text-2xl font-bold text-gray-800">
            Manage Admins & Permissions
          </h1>
          <p className="text-sm text-gray-700">
            View and manage the users who have admin access to the Ridgetop
            Botanicals dashboard.
          </p>

          {error && (
            <div className="rounded-md border border-red-500 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-gray-700 text-sm">Loading admins…</div>
          ) : admins.length === 0 ? (
            <div className="text-gray-700 text-sm">
              No admins found. Once your backend exposes the admin users
              endpoint, they will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-800">
                      Name
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-800">
                      Email
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-800">
                      Role
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-800">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-800">
                      Joined
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-800">
                      Last Login
                    </th>
                    {/* Future: actions */}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr
                      key={admin.id}
                      className="border-t border-gray-200 hover:bg-gray-50"
                    >
                      <td className="px-3 py-2 text-gray-900">
                        {admin.name || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {admin.email}
                      </td>
                      <td className="px-3 py-2 text-gray-800 capitalize">
                        {admin.role || "admin"}
                      </td>
                      <td className="px-3 py-2 text-gray-800">
                        {admin.active ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {formatDate(admin.createdAt)}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {formatDate(admin.lastLoginAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Future: add invite / revoke controls here */}
        </div>
      </div>
    </Layout>
  );
}
